/// Zustand store for UI layout state: panel visibility, layout mode,
/// sidebar/panel dimensions, and notifications.

import { create } from "zustand";

// ---------------------------------------------------------------------------
// Layout types
// ---------------------------------------------------------------------------

/** Layout mode for the main editor area. */
export type LayoutMode = "single" | "split-horizontal" | "split-vertical";

/** Which side panel is currently visible. */
type SidePanelId = "properties" | "palette" | "project" | "console" | "none";

/** A UI notification. */
export interface Notification {
  id: string;
  type: "info" | "success" | "warning" | "error";
  message: string;
  timestamp: number;
  duration?: number; // ms, undefined = persistent
}

// ---------------------------------------------------------------------------
// Store state shape
// ---------------------------------------------------------------------------

interface UIStoreState {
  /** Current layout mode for the editor area. */
  layoutMode: LayoutMode;
  /** Whether the left sidebar is visible. */
  leftSidebarVisible: boolean;
  /** Width of the left sidebar in pixels. */
  leftSidebarWidth: number;
  /** Whether the right panel is visible. */
  rightPanelVisible: boolean;
  /** Width of the right panel in pixels. */
  rightPanelWidth: number;
  /** Which right panel is active. */
  activeRightPanel: SidePanelId;
  /** Whether the bottom panel (console) is visible. */
  bottomPanelVisible: boolean;
  /** Height of the bottom panel in pixels. */
  bottomPanelHeight: number;
  /** Whether the toolbar is visible. */
  toolbarVisible: boolean;
  /** Whether the status bar is visible. */
  statusBarVisible: boolean;
  /** Current notifications. */
  notifications: Notification[];
  /** Maximum notifications to display. */
  maxNotifications: number;
  /** Global loading state (e.g., WASM loading). */
  isLoading: boolean;
  /** Loading message. */
  loadingMessage: string;
}

interface UIStoreActions {
  // Layout
  setLayoutMode: (mode: LayoutMode) => void;

  // Left sidebar
  toggleLeftSidebar: () => void;
  setLeftSidebarWidth: (width: number) => void;

  // Right panel
  toggleRightPanel: () => void;
  setRightPanelWidth: (width: number) => void;
  setActiveRightPanel: (panel: SidePanelId) => void;
  openRightPanel: (panel: SidePanelId) => void;

  // Bottom panel
  toggleBottomPanel: () => void;
  setBottomPanelHeight: (height: number) => void;

  // Toolbar & status bar
  toggleToolbar: () => void;
  toggleStatusBar: () => void;

  // Notifications
  addNotification: (notification: Omit<Notification, "id" | "timestamp">) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;

  // Loading
  setLoading: (isLoading: boolean, message?: string) => void;

  // Reset
  reset: () => void;
}

type UIStore = UIStoreState & UIStoreActions;

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialState: UIStoreState = {
  layoutMode: "single",
  leftSidebarVisible: true,
  leftSidebarWidth: 240,
  rightPanelVisible: true,
  rightPanelWidth: 300,
  activeRightPanel: "properties",
  bottomPanelVisible: false,
  bottomPanelHeight: 200,
  toolbarVisible: true,
  statusBarVisible: true,
  notifications: [],
  maxNotifications: 10,
  isLoading: false,
  loadingMessage: "",
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useUIStore = create<UIStore>((set) => ({
  ...initialState,

  // -------------------------------------------------------------------------
  // Layout
  // -------------------------------------------------------------------------

  setLayoutMode: (mode) => set({ layoutMode: mode }),

  // -------------------------------------------------------------------------
  // Left sidebar
  // -------------------------------------------------------------------------

  toggleLeftSidebar: () => set((state) => ({ leftSidebarVisible: !state.leftSidebarVisible })),

  setLeftSidebarWidth: (width) => set({ leftSidebarWidth: Math.max(160, Math.min(480, width)) }),

  // -------------------------------------------------------------------------
  // Right panel
  // -------------------------------------------------------------------------

  toggleRightPanel: () => set((state) => ({ rightPanelVisible: !state.rightPanelVisible })),

  setRightPanelWidth: (width) => set({ rightPanelWidth: Math.max(200, Math.min(600, width)) }),

  setActiveRightPanel: (panel) => set({ activeRightPanel: panel }),

  openRightPanel: (panel) => set({ rightPanelVisible: true, activeRightPanel: panel }),

  // -------------------------------------------------------------------------
  // Bottom panel
  // -------------------------------------------------------------------------

  toggleBottomPanel: () => set((state) => ({ bottomPanelVisible: !state.bottomPanelVisible })),

  setBottomPanelHeight: (height) =>
    set({ bottomPanelHeight: Math.max(100, Math.min(500, height)) }),

  // -------------------------------------------------------------------------
  // Toolbar & status bar
  // -------------------------------------------------------------------------

  toggleToolbar: () => set((state) => ({ toolbarVisible: !state.toolbarVisible })),
  toggleStatusBar: () => set((state) => ({ statusBarVisible: !state.statusBarVisible })),

  // -------------------------------------------------------------------------
  // Notifications
  // -------------------------------------------------------------------------

  addNotification: (notification) =>
    set((state) => {
      const newNotification: Notification = {
        ...notification,
        id: `notif-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        timestamp: Date.now(),
      };
      const notifications = [newNotification, ...state.notifications];
      if (notifications.length > state.maxNotifications) {
        notifications.length = state.maxNotifications;
      }
      return { notifications };
    }),

  removeNotification: (id) =>
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    })),

  clearNotifications: () => set({ notifications: [] }),

  // -------------------------------------------------------------------------
  // Loading
  // -------------------------------------------------------------------------

  setLoading: (isLoading, message) => set({ isLoading, loadingMessage: message ?? "" }),

  // -------------------------------------------------------------------------
  // Reset
  // -------------------------------------------------------------------------

  reset: () => set(initialState),
}));
